import React, { useCallback, useMemo } from 'react';

import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';

import { useI18n } from '@change-corgi/core-react/i18n';

import { getDefaultStripeErrorValidation, stripeErrorI18nKey, stripeInputName } from '#src/app/shared/utils/payments';

import { StyleWrapper } from '../../../../components/StyleWrapper';
import { CardElementWrapper } from '../../../components/StripeElementWrappers';
import type { SingleFormProps } from '../../types';

type ComponentProps = Pick<
	SingleFormProps,
	'onCardDetailsFieldFocus' | 'validationErrors' | 'setValidationErrors' | 'whiteBackground' | 'disabled'
>;

export const SingleForm: React.FC<ComponentProps> = (props) => {
	const { translate, typedTranslate } = useI18n();

	const { onCardDetailsFieldFocus, validationErrors, setValidationErrors, whiteBackground, disabled } = props;

	const onChange = useCallback(
		(e: StripeCardElementChangeEvent) => {
			setValidationErrors?.({
				...getDefaultStripeErrorValidation(),
				[stripeInputName(e.elementType) as string]: e.error ? stripeErrorI18nKey(e.error) : null,
			});
		},
		[setValidationErrors],
	);

	const error = useMemo(() => {
		if (validationErrors.card) {
			return validationErrors.card;
		}
		if (validationErrors.card_number) {
			return validationErrors.card_number;
		}
		if (validationErrors.card_expiration) {
			return validationErrors.card_expiration;
		}
		if (validationErrors.card_cvc) {
			return validationErrors.card_cvc;
		}
		return null;
	}, [validationErrors]);

	const labelText = translate('corgi.co.promote.card_details');

	return (
		<div data-qa="credit-card-details">
			<StyleWrapper
				label={labelText}
				errorMessage={error ? typedTranslate(error) : undefined}
				onFocus={onCardDetailsFieldFocus}
				whiteBackground={whiteBackground}
				disabled={disabled}
			>
				<CardElementWrapper onChange={onChange} disabled={disabled} />
			</StyleWrapper>
		</div>
	);
};
